import { useState } from 'react'
import StudioMark from './StudioMark'
import BuildCredit from './BuildCredit'
import { useQuery } from '@tanstack/react-query'
import { Link, NavLink } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { fetchContent, has, publicHref, text } from '../api/content'
import AnnouncementBanner from './AnnouncementBanner'
import ClosedTodayBanner from './ClosedTodayBanner'
import JackrabbitPortalLink from './JackrabbitPortalLink'
import { StudioAddress } from './StudioAddress'
import { SocialFooterLinks, SocialHeaderLinks, SocialMenuLinks } from './SocialLinks'

/**
 * Chrome for everything a family sees.
 *
 * Deliberately not the studio nav. A prospective parent has no account, no
 * calendar and no dancer — showing them "Conflicts" and "Import" would be
 * confusing at best.
 *
 * **No Register link, by design (D-111).** Registering happens on Jackrabbit
 * while it still owns billing, and it is reached from a *class* — a nav item
 * would land a parent on a form with no idea what they are signing up for.
 * Tryouts left with it.
 */
export default function PublicShell({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false)

  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })

  const links = [
    { to: publicHref('/'), label: 'Home', end: true },
    { to: publicHref('/classes'), label: 'Classes', end: false },
    { to: publicHref('/about'), label: 'About', end: false },
  ]

  const link = ({ isActive }: { isActive: boolean }) =>
    `rounded-lg px-3 py-1.5 text-[13.5px] font-semibold no-underline transition-colors ${
      isActive ? 'bg-accent-soft text-accent-2' : 'text-ink-2 hover:text-ink'
    }`

  return (
    /*
     * Always dark, and no choice offered (D-200).
     *
     * The owner and a parent both said the public site simply looks better this
     * way, and a marketing page whose character changes with the visitor's OS
     * setting is two first impressions rather than one.
     *
     * Declared here rather than on <html> so it does not fight the signed-in
     * preference: custom properties inherit from the nearest ancestor that sets
     * them, so this subtree is dark even for somebody who has chosen light for
     * the app — which is also what makes the catalog preview under Website show
     * exactly what families see.
     *
     * `bg-ground` because `body` still paints from the root's tokens; without
     * it the page would sit on a light sheet at the edges.
     */
    <div data-theme="dark" className="flex min-h-full flex-col bg-ground text-ink">
      {/* Above her own banner: a closure outranks whatever else is being said. */}
      <ClosedTodayBanner />
      <AnnouncementBanner />

      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex max-w-[56rem] items-center gap-4 px-5 py-3">
          <Link to={publicHref('/')} className="flex items-center gap-3 no-underline">
            {/*
              The studio's own logo, once it is ours rather than Wix's. It is
              black and pink on a white ground with no transparency, so it sits
              on a white tile — otherwise it renders as a white box on the dark
              theme. Falls back to a wordmark when no file is set.
            */}
            {has(content, 'site.logo') ? (
              <img
                src={text(content, 'site.logo')}
                alt="Cabot Dance Academy"
                className="h-14 w-auto shrink-0 rounded-lg bg-white px-2 py-1 sm:h-16"
              />
            ) : (
              <StudioMark />
            )}

            <span className="min-w-0">
              <span className="block text-[13.5px] font-semibold text-ink">Cabot Dance Academy</span>
              {has(content, 'home.tagline') && (
                <span className="block text-[11px] text-ink-3">{text(content, 'home.tagline')}</span>
              )}
            </span>
          </Link>

          <nav className="ml-auto hidden items-center gap-1 sm:flex">
            {links.map((entry) => (
              <NavLink key={entry.to} to={entry.to} end={entry.end} className={link}>
                {entry.label}
              </NavLink>
            ))}

            {/*
              Between the pages and the account controls, deliberately. The
              studio's Facebook and Instagram are where families already look
              for it, and at the foot of the page they were the faintest thing
              on it (D-129).
            */}
            <span className="mx-1 h-5 w-px bg-line" aria-hidden="true" />
            <SocialHeaderLinks content={content} />

            <span className="mx-1 h-5 w-px bg-line" aria-hidden="true" />

            <NavLink to={publicHref('/signin')} className={link}>
              Sign in
            </NavLink>
          </nav>

          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
            aria-expanded={open}
            className="ml-auto grid size-9 place-items-center rounded-lg text-ink-2 hover:bg-surface-2 sm:hidden"
          >
            {open ? <X size={18} strokeWidth={2.5} /> : <Menu size={18} strokeWidth={2.5} />}
          </button>
        </div>

        {open && (
          <nav className="flex flex-col gap-1 border-t border-line px-5 py-3 sm:hidden">
            {[...links, { to: publicHref('/signin'), label: 'Sign in', end: false }].map((entry) => (
              <NavLink
                key={entry.to}
                to={entry.to}
                end={entry.end}
                onClick={() => setOpen(false)}
                className={link}
              >
                {entry.label}
              </NavLink>
            ))}

            {/* Named here rather than shown as bare icons — the phone header
                has no room for them, but the open menu has room for words. */}
            <span className="my-1 h-px bg-line" aria-hidden="true" />
            <SocialMenuLinks content={content} onNavigate={() => setOpen(false)} />
          </nav>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-line bg-surface-2">
        <div className="mx-auto max-w-[56rem] px-5 py-6">
          {/*
            Its own row, above the small print (D-129). Sharing a line with the
            phone number in the faintest ink is exactly how these got lost, and
            they are the studio's own photographs of its own dancers — closer to
            content than to a legal notice.
          */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <SocialFooterLinks content={content} />

            {has(content, 'home.tagline') && (
              <span className="font-display text-[15px] text-accent-2">
                {text(content, 'home.tagline')}
              </span>
            )}

            {has(content, 'studio.phone') && (
              <a
                href={`tel:${text(content, 'studio.phone').replace(/[^\d+]/g, '')}`}
                className="ml-auto text-[13px] font-semibold text-ink-2 no-underline hover:text-accent-2"
              >
                {text(content, 'studio.phone')}
              </a>
            )}
          </div>

          {/*
            The Jackrabbit parent portal, for families who already have one
            (D-270).

            <b>Not a contradiction of "no Register link" above.</b> That rule is
            about a *prospective* parent, who has to reach registration from a
            class or they land on a form with no idea what they are signing up
            for. This is the opposite person: an existing family looking to pay
            a bill or update a card, who currently has to remember a Jackrabbit
            address nobody has written down for them.

            <b>In the footer, not beside "Sign in".</b> Two sign-in links in one
            header, one of ours and one of somebody else's, is a choice a parent
            should never have to make — and the wrong guess lands her on a login
            her password does not work for.

            The same content key the parent portal uses (D-259), so the address
            is one field for Tonya rather than two that can disagree.
          */}
          {/*
            **The same component the parent portal draws** (D-396).

            `asked`: *"the link on the public footer for the jackrabbit parent
            portal should probably match the pink one that is on the parent
            portal side."* It was a quiet line of body text here and an accent
            card there, from one content key — two copies of one idea, and this
            was the copy that never received D-313's fix after testing reported
            the quiet version as hidden.

            The wrapper keeps the footer rule and the spacing; everything inside
            it is shared, so the next change to either reaches both.
          */}
          {has(content, 'portal.jackrabbit.url') && (
            <div className="mt-4 border-t border-line pt-4">
              <JackrabbitPortalLink />
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-baseline gap-x-4 gap-y-1 border-t border-line pt-4 text-[12.5px] text-ink-3">
            <p>
              &copy; {new Date().getFullYear()} Cabot Dance Academy
              {/* Linked, exactly as the home page links it (D-499). */}
              {has(content, 'studio.address') && (
                <>
                  {' '}
                  &middot; <StudioAddress address={text(content, 'studio.address')} />
                </>
              )}
            </p>

            {/*
              Who built it, on its own line and in its own colour (D-222,
              amended D-224).

              <b>Not the studio's pink.</b> That is her identity (D-201), and
              marking somebody else's credit with it borrows the wrong thing —
              so this carries the builder's bronze instead, which catches a
              scanning eye without competing with her brand.

              <b>Words only — no mark, after trying two.</b> The detailed render
              was unreadable at 22px, and a simplified outline still needed its
              hairlines thickened to survive the pixel grid. Both were tried in
              place and neither earned its space: at this size a mark is either
              recognised instantly or it is a smudge beside a business name,
              and a smudge is worse than nothing. The words do the job on their
              own, and the bronze is what makes them findable.

              <b>A literal, deliberately (D-224).</b> A-16 exists so the studio
              never needs a developer to change its own words — but these are
              not the studio's words, and a credit somebody can quietly clear
              from a settings screen is not a credit. Kept apart from the
              copyright beside it too: the studio owns its content and its
              photographs, and that notice has to keep saying so.
            */}
            <BuildCredit tone="bronze" />
          </div>
        </div>
      </footer>
    </div>
  )
}
